import { Static, Type } from '@sinclair/typebox';
import { DetectionSourceSummarySchema } from '../business_detection_event';
import { LeadQualificationSchema } from '../assistant';
import { OnboardingIntention } from '../translation_namespace/onboarding_intention_namespace';
import { OnboardingFlow } from '../translation_namespace/onboarding_flow_namespace';
import { PulseTextKind } from '../translation_namespace/pulse_text_kind_namespace';
import { PrestationPaymentType } from '../translation_namespace/prestation_namespace';
import { PlaceKind } from '../translation_namespace/place_kind_namespace';
import { ExtractedPrestationSchema, WebsitePreviewExtractedSchema } from '../website_preview_extracted';

/**
 * Schémas TypeBox des endpoints du OnboardingModule. Source de vérité unique :
 * validation backend, Swagger, types frontend (Orval). Cf.
 * docs/onboarding-implementation-plan.md § 3.
 */

/** Union de littéraux pour l'enum d'intention (pattern `pack_namespace`). */
export const OnboardingIntentionSchema = Type.Union(
  Object.values(OnboardingIntention).map((value) => Type.Literal(value)),
);

/** Parcours servi au pro (`professional.onboarding_flow`). */
export const OnboardingFlowSchema = Type.Union(Object.values(OnboardingFlow).map((value) => Type.Literal(value)));

// ─── Tunnel obligatoire (parcours V2) : reports, règle de paiement ────────────

/** Clés de report — miroir de `OnboardingDeferredKind` (onboarding-registry.types). */
export const OnboardingDeferredKindSchema = Type.Union([
  Type.Literal('about'),
  Type.Literal('share_link'),
  Type.Literal('website_style'),
  Type.Literal('fiche_google'),
  Type.Literal('stripe'),
  Type.Literal('logo_photos'),
]);

/**
 * Un report posé pendant le tunnel (« ignorer pour l'instant ») — élément du JSON
 * `onboarding_state.deferred_items`. Un report ouvert (`closed_at` null) remonte dans
 * « Et maintenant » après le tunnel ; il se ferme quand la chose est faite ou abandonnée.
 */
export const OnboardingDeferredItemSchema = Type.Object({
  kind: OnboardingDeferredKindSchema,
  /** ISO 8601. */
  created_at: Type.String(),
  /** Raison choisie par le pro (ex. `no_google_profile_yet`) — jamais de texte libre. */
  reason: Type.Optional(Type.String({ maxLength: 64 })),
  closed_at: Type.Optional(Type.Union([Type.String(), Type.Null()])),
});
export type OnboardingDeferredItemObject = Static<typeof OnboardingDeferredItemSchema>;

/**
 * Règle de paiement choisie à l'étape 6 AVANT Stripe (`onboarding_state.pending_payment_rule`).
 * Appliquée aux prestations (`payment_type` / `percentage`) dès que `charges_enabled` passe à
 * vrai ; tant que Stripe n'encaisse pas, aucune prestation ne doit porter un type payant.
 */
export const OnboardingPendingPaymentRuleSchema = Type.Object({
  payment_type: Type.Union(Object.values(PrestationPaymentType).map((value) => Type.Literal(value))),
  /** Pourcentage d'acompte (DEPOSIT) — 1 à 100. */
  percentage: Type.Optional(Type.Integer({ minimum: 1, maximum: 100 })),
  /** `all` = toutes les prestations ; `selected` = seulement `prestation_ids`. */
  scope: Type.Union([Type.Literal('all'), Type.Literal('selected')]),
  prestation_ids: Type.Optional(Type.Array(Type.Integer(), { maxItems: 200 })),
  /** ISO 8601. */
  recorded_at: Type.String(),
  /** ISO 8601 — posé quand la règle a été appliquée aux prestations (Stripe actif). */
  applied_at: Type.Optional(Type.Union([Type.String(), Type.Null()])),
});
export type OnboardingPendingPaymentRuleObject = Static<typeof OnboardingPendingPaymentRuleSchema>;

// ─── Réponse : état d'onboarding ──────────────────────────────────────────────

export const OnboardingStateResponseSchema = Type.Object({
  /** Parcours servi au pro — `v2` = tunnel obligatoire tant que `tunnel_completed_at` est nul. */
  onboarding_flow: OnboardingFlowSchema,
  /** ISO — fin du tunnel (parcours V2). Null = tunnel en cours, ou parcours V1. */
  tunnel_completed_at: Type.Union([Type.String(), Type.Null()]),
  /** Reports posés pendant le tunnel (ouverts et fermés). */
  deferred_items: Type.Array(OnboardingDeferredItemSchema),
  pending_payment_rule: Type.Union([OnboardingPendingPaymentRuleSchema, Type.Null()]),
  /** ISO — bonus « tout prêt en 7 jours » acquis. */
  bonus_earned_at: Type.Union([Type.String(), Type.Null()]),
  /** ISO — le pro a partagé / copié son lien de réservation (étape 3). */
  link_shared_at: Type.Union([Type.String(), Type.Null()]),
  /** Intention captée — null tant que l'écran d'entrée n'a pas été passé. */
  intention: Type.Union([OnboardingIntentionSchema, Type.Null()]),
  /** Identifiant de l'étape du wizard en cours — null hors parcours. */
  current_step: Type.Union([Type.String(), Type.Null()]),
  /** Identifiants des étapes du wizard terminées. */
  completed_steps: Type.Array(Type.String()),
  /** Types d'opt-out validés (« Je ne veux pas proposer »). */
  opt_outs: Type.Array(Type.String()),
  /** Identifiants des encarts adaptatifs fermés par le pro. */
  dismissed_nudges: Type.Array(Type.String()),
  /**
   * Horodatage ISO du consentement à la détection — null tant que le pro n'a pas
   * répondu à l'écran de consentement. Exposé pour que la coquille du wizard sache,
   * sans fetch supplémentaire, si elle doit afficher l'écran de consentement.
   */
  detection_consent_at: Type.Union([Type.String(), Type.Null()]),
  /** Périmètre accepté (`'declined'` / `'auto'` / `'auto+url+photos'` …) — null si pas répondu. */
  detection_consent_scope: Type.Union([Type.String(), Type.Null()]),
});
export type OnboardingStateResponseObject = Static<typeof OnboardingStateResponseSchema>;

// ─── Réponse : score de complétion ────────────────────────────────────────────

export const OnboardingScoreTaskSchema = Type.Object({
  id: Type.String(),
  label_key: Type.String(),
  criticality: Type.String(),
  /** Score de la tâche, 0 à 100. */
  score: Type.Number(),
  done: Type.Boolean(),
  opted_out: Type.Boolean(),
  /**
   * Gain estimé de score global (en points) si la tâche est complétée.
   * Optionnel pour ne pas casser les clients Orval déjà générés (regen à faire).
   */
  score_impact: Type.Optional(Type.Number()),
  /**
   * Détail de progression chiffré (« done / total ») pour les tâches multi-items
   * (ex. 6/8 prestations avec photo). Absent pour les tâches binaires.
   */
  progress_detail: Type.Optional(Type.Object({ done: Type.Number(), total: Type.Number() })),
  menu_route: Type.String(),
  menu_sub_tab: Type.Optional(Type.String()),
});
export type OnboardingScoreTaskObject = Static<typeof OnboardingScoreTaskSchema>;

export const OnboardingScoreSubRubriqueSchema = Type.Object({
  id: Type.String(),
  label_key: Type.String(),
  /** Score de la sous-rubrique, 0 à 100. */
  score: Type.Number(),
});
export type OnboardingScoreSubRubriqueObject = Static<typeof OnboardingScoreSubRubriqueSchema>;

export const OnboardingScoreFamilySchema = Type.Object({
  id: Type.String(),
  name_key: Type.String(),
  icon: Type.String(),
  order: Type.Number(),
  /** Score de la famille, 0 à 100. */
  score: Type.Number(),
  menu_route: Type.String(),
  sub_rubriques: Type.Array(OnboardingScoreSubRubriqueSchema),
  /** Tâches incomplètes à pousser comme sous-actions dans le composant score. */
  sub_actions: Type.Array(OnboardingScoreTaskSchema),
});
export type OnboardingScoreFamilyObject = Static<typeof OnboardingScoreFamilySchema>;

export const OnboardingScoreResponseSchema = Type.Object({
  /** Score global, 0 à 100, plafonné. */
  global_score: Type.Number(),
  families: Type.Array(OnboardingScoreFamilySchema),
});
export type OnboardingScoreResponseObject = Static<typeof OnboardingScoreResponseSchema>;

// ─── Requêtes ─────────────────────────────────────────────────────────────────

export const SetOnboardingIntentionRequestSchema = Type.Object({
  intention: OnboardingIntentionSchema,
});
export type SetOnboardingIntentionRequestObject = Static<typeof SetOnboardingIntentionRequestSchema>;

export const OnboardingOptOutRequestSchema = Type.Object({
  /** Type d'opt-out (`OnboardingTask.opt_out_kind`), ex : `packs`. */
  opt_out_kind: Type.String({ minLength: 1, maxLength: 50 }),
});
export type OnboardingOptOutRequestObject = Static<typeof OnboardingOptOutRequestSchema>;

export const CompleteOnboardingStepRequestSchema = Type.Object({
  /** Identifiant de l'étape du wizard (`OnboardingStep.id`). */
  step_id: Type.String({ minLength: 1, maxLength: 50 }),
});
export type CompleteOnboardingStepRequestObject = Static<typeof CompleteOnboardingStepRequestSchema>;

export const DismissOnboardingNudgeRequestSchema = Type.Object({
  /** Identifiant de l'encart adaptatif à masquer. */
  nudge_id: Type.String({ minLength: 1, maxLength: 80 }),
});
export type DismissOnboardingNudgeRequestObject = Static<typeof DismissOnboardingNudgeRequestSchema>;

/** Une plage horaire continue `HH:mm`–`HH:mm` (ex. matinée avant la coupure déjeuner). */
export const OnboardingOpeningRangeSchema = Type.Object({
  start: Type.String({ pattern: '^([01]\\d|2[0-3]):[0-5]\\d$' }),
  end: Type.String({ pattern: '^([01]\\d|2[0-3]):[0-5]\\d$' }),
});
export type OnboardingOpeningRangeObject = Static<typeof OnboardingOpeningRangeSchema>;

/** Un jour de la grille hebdomadaire envoyée par le wizard.
 *  - `start`/`end` : plage UNIQUE héritée (détection / clients historiques), `HH:mm`.
 *  - `ranges` : plages MULTIPLES (coupure déjeuner) — prioritaires si présentes et
 *    cohérentes. Toutes absentes ⇒ jour fermé ou heures inconnues (fallback sectoriel). */
export const OnboardingOpeningDaySchema = Type.Object({
  open: Type.Boolean(),
  start: Type.Optional(Type.String({ pattern: '^([01]\\d|2[0-3]):[0-5]\\d$' })),
  end: Type.Optional(Type.String({ pattern: '^([01]\\d|2[0-3]):[0-5]\\d$' })),
  ranges: Type.Optional(Type.Array(OnboardingOpeningRangeSchema, { maxItems: 4 })),
});
export type OnboardingOpeningDayObject = Static<typeof OnboardingOpeningDaySchema>;

/**
 * Wizard 4.B — grille hebdomadaire « ouvert / fermé », horaires précis optionnels.
 *
 * `days` = autorité ouvert/fermé (toggles du pro). `hours` (optionnel, même index
 * 0 = lundi … 6 = dimanche) porte les **créneaux précis détectés** : quand un jour
 * ouvert fournit `start`+`end` cohérents, ils priment ; sinon le backend retombe
 * sur les défauts sectoriels (`resolveSector` + `SECTORAL_DEFAULTS`). Omettre `hours`
 * = comportement historique (100 % sectoriel). Le pro affine ensuite dans l'Agenda.
 */
export const UpsertOnboardingOpeningHoursRequestSchema = Type.Object({
  days: Type.Array(Type.Boolean(), { minItems: 7, maxItems: 7 }),
  hours: Type.Optional(Type.Array(OnboardingOpeningDaySchema, { minItems: 7, maxItems: 7 })),
  /**
   * Wizard 4.A — modalités de RDV sélectionnées. Le backend matérialise un
   * `pro_place` par modalité (idempotent par `kind`). Omis ou vide ⇒ établissement
   * seul (comportement historique). Les modalités du pro sont ensuite dérivées de
   * `SELECT DISTINCT kind FROM pro_place`.
   */
  place_kinds: Type.Optional(Type.Array(Type.Enum(PlaceKind), { minItems: 1, maxItems: 4, uniqueItems: true })),
});
export type UpsertOnboardingOpeningHoursRequestObject = Static<typeof UpsertOnboardingOpeningHoursRequestSchema>;

/** Wizard 3 — une catégorie validée + ses prestations (par nom). */
export const OnboardingCatalogDraftSchema = Type.Object({
  name: Type.String({ minLength: 1, maxLength: 255 }),
  prestations: Type.Array(Type.String({ minLength: 1, maxLength: 255 }), { minItems: 1, maxItems: 50 }),
});
export type OnboardingCatalogDraftObject = Static<typeof OnboardingCatalogDraftSchema>;

/**
 * Wizard 3 — confirmation du catalogue à la VALIDATION (et non plus à la détection).
 * - `categories` présent ⇒ le pro a saisi/édité l'arborescence → le backend
 *   reconstruit le catalogue (wipe + recréation, noms uniquement → prix/durées par
 *   défaut, à affiner ensuite).
 * - `categories` absent ⇒ matérialisation depuis le snapshot détecté (idempotente,
 *   prix/durées détectés conservés).
 */
/**
 * Une prestation de l'étape 1 du tunnel (écran 11) : prix et durée OBLIGATOIRES (sans eux, pas de
 * lien réservable). Les défauts de la verticale arrivent pré-remplis et marqués « à confirmer »
 * côté écran ; ce qui est envoyé ici est ce que le pro a validé. `prestation_id` présent ⇒ mise à
 * jour de la prestation existante (jamais de suppression / recréation).
 */
export const OnboardingTunnelPrestationSchema = Type.Object({
  prestation_id: Type.Optional(Type.Integer({ minimum: 1 })),
  name: Type.String({ minLength: 1, maxLength: 255 }),
  /** Absent ⇒ prestation « sur devis » (`QUOTE`) : le pro valide la liste sans être bloqué par un prix inconnu. */
  price_eur: Type.Optional(Type.Number({ minimum: 0, maximum: 100000 })),
  duration_min: Type.Integer({ minimum: 5, maximum: 24 * 60 }),
  /** Famille détectée (« Soins du visage ») ; absente ⇒ catégorie « Mes prestations ». */
  category: Type.Optional(Type.String({ minLength: 1, maxLength: 255 })),
  description: Type.Optional(Type.String({ maxLength: 2000 })),
});
export type OnboardingTunnelPrestationObject = Static<typeof OnboardingTunnelPrestationSchema>;

export const ConfirmOnboardingCatalogRequestSchema = Type.Object({
  /** Wizard v1 (validation-first) : arborescence noms-only, reconstruction wipe + create. */
  categories: Type.Optional(Type.Array(OnboardingCatalogDraftSchema, { minItems: 1, maxItems: 20 })),
  /** Tunnel v2 (étape 1) : upsert prestation par prestation, durée obligatoire, prix absent = sur devis. */
  prestations: Type.Optional(Type.Array(OnboardingTunnelPrestationSchema, { minItems: 1, maxItems: 100 })),
});
export type ConfirmOnboardingCatalogRequestObject = Static<typeof ConfirmOnboardingCatalogRequestSchema>;

/**
 * Wizard — confirmation des photos détectées à la VALIDATION (et non à la détection).
 * `kept_indices` = positions des photos du snapshot détecté à conserver (alignées sur
 * `extracted.photos`, même ordre que le store frontend). Le backend écrit la galerie
 * `professional.pictures` avec ces photos uniquement (curation explicite par le pro).
 */
export const ConfirmOnboardingPhotosRequestSchema = Type.Object({
  kept_indices: Type.Array(Type.Integer({ minimum: 0 }), { maxItems: 100 }),
});
export type ConfirmOnboardingPhotosRequestObject = Static<typeof ConfirmOnboardingPhotosRequestSchema>;

/**
 * Réponse de la génération du site (Lot 2). `generated` distingue une vraie
 * génération (draft créé) d'un no-op idempotent (le pro avait déjà un draft, on
 * ne l'écrase pas). `from_detection` indique si des données détectées ont nourri
 * le draft (hero, bio) — sinon on est parti d'un gabarit sectoriel vierge.
 */
export const OnboardingGenerateWebsiteResponseSchema = Type.Object({
  success: Type.Boolean(),
  generated: Type.Boolean(),
  from_detection: Type.Boolean(),
});
export type OnboardingGenerateWebsiteResponseObject = Static<typeof OnboardingGenerateWebsiteResponseSchema>;

/**
 * Détection à partir de sources fournies manuellement par le pro (écran no-match) :
 * une URL (site / fiche Planity / Insta) et/ou des images (clés S3 d'uploads :
 * carte de visite, flyer, capture…). Au moins l'une des deux doit être renseignée.
 */
export const OnboardingDetectFromSourcesRequestSchema = Type.Object({
  url: Type.Optional(Type.String()),
  image_keys: Type.Optional(Type.Array(Type.String())),
});
export type OnboardingDetectFromSourcesRequestObject = Static<typeof OnboardingDetectFromSourcesRequestSchema>;

/**
 * Consentement explicite à la détection (écran d'entrée du wizard, AVANT toute
 * source de scraping). `granted: false` = refus tracé → saisie manuelle pure.
 * `url`/`has_photos` ne sont lus que si `granted: true` : ils enrichissent le
 * périmètre persisté (`detection_consent_scope`) et la stratégie de sources.
 * Cf. docs/to_implement/onboarding-consent-and-catalog-plan.md (Lot A1/A2).
 */
export const OnboardingDetectionConsentRequestSchema = Type.Object({
  granted: Type.Boolean(),
  url: Type.Optional(Type.String({ maxLength: 2048 })),
  has_photos: Type.Optional(Type.Boolean()),
});
export type OnboardingDetectionConsentRequestObject = Static<typeof OnboardingDetectionConsentRequestSchema>;

/**
 * Valeur sentinelle de `professional.detection_consent_scope` traçant un refus
 * explicite du pro (vs. un consentement accordé `auto[+url][+photos]`, ou `null`
 * = pas encore répondu). Source unique partagée front/back pour éviter toute dérive
 * du littéral `'declined'` (gate wizard, garde backend du scraping).
 */
export const DETECTION_CONSENT_DECLINED_SCOPE = 'declined';

/**
 * Valeur sentinelle de `professional.detection_consent_scope` pour un pro **issu du
 * funnel preview→compte** : ses données ont déjà été récupérées (avec son accord) lors
 * de la conversation Pulse et injectées au signup (`hydrateProFromLeadPreview` →
 * site `draft_config` + catalogue). Le consentement est donc déjà acquis → on saute
 * l'écran `DataConsentStep`, et comme la détection a DÉJÀ tourné on ne relance AUCUN
 * scraping (le wizard ne re-déclenche pas la détection pour ce scope). À distinguer de
 * `'declined'` (refus explicite) et des scopes `auto[+url][+photos]` (accord + scrape à venir).
 */
export const DETECTION_CONSENT_PREVIEW_SCOPE = 'preview_hydrated';

/**
 * Valeur de `professional.detection_consent_scope` pour un pro du **parcours v2** (tunnel) :
 * le texte légal de l'inscription (quiz écran 7, page register) annonce qu'on retrouve ses
 * informations publiques (fiche Google, site, réseaux) pour préparer son espace ; la détection
 * part en arrière-plan dès l'inscription (écran 9 du tunnel) et le pro valide ce qui a été trouvé
 * avant que quoi que ce soit n'entre dans son catalogue ou son profil.
 */
export const DETECTION_CONSENT_SIGNUP_SCOPE = 'signup_auto';

/** Résultat (forme `done` de détection, images déjà signées Thumbor pour le front). */
export const OnboardingDetectFromSourcesResponseSchema = Type.Object({
  no_match: Type.Boolean(),
  extracted: Type.Optional(WebsitePreviewExtractedSchema),
});
export type OnboardingDetectFromSourcesResponseObject = Static<typeof OnboardingDetectFromSourcesResponseSchema>;

/**
 * Porte B — qualification captée par Pulse pendant la conversation de
 * découverte. Sert à pré-remplir les champs du wizard (`nom_pro`, `metier`,
 * `ville`, …) — cf. `OnboardingTask.prefill` dans le registre.
 *
 * `qualification` est partiel : tous les champs sont optionnels, et l'objet
 * peut être vide si le pro n'a pas démarré via Pulse (signup direct).
 */
export const OnboardingLeadQualificationResponseSchema = Type.Object({
  qualification: Type.Partial(LeadQualificationSchema),
});
export type OnboardingLeadQualificationResponseObject = Static<typeof OnboardingLeadQualificationResponseSchema>;

/**
 * Prestations par défaut « exemples de votre métier » proposées à l'étape Prestations
 * du wizard quand la détection n'a rien trouvé. Résolues backend-side depuis l'activité
 * principale du pro (même contenu que celui injecté à la génération du site).
 */
export const OnboardingDefaultPrestationsResponseSchema = Type.Object({
  prestations: Type.Array(ExtractedPrestationSchema),
});
export type OnboardingDefaultPrestationsResponseObject = Static<typeof OnboardingDefaultPrestationsResponseSchema>;

// ─── Tunnel v4 · lot 2 bis : reprise (écran 13), résumé réel (18 bis / 18 quater), semaine (16 ter) ──

/**
 * Brouillon d'une étape du tunnel, sauvegardé à chaque champ (autosave) — élément du JSON
 * `onboarding_state.tunnel_draft`, indexé par `step_id`. Les `fields` sont propres à l'écran (lieux
 * cochés, rayon, horaires, règle de paiement, à-propos…) : le serveur les stocke tels quels, c'est
 * l'écran qui sait les relire. `missing_field` nomme le champ exact qui manque pour valider
 * l'étape : l'écran 13 « Reprendre » l'ouvre directement.
 */
export const OnboardingTunnelDraftSchema = Type.Object({
  step_id: Type.String({ minLength: 1, maxLength: 32 }),
  fields: Type.Record(Type.String({ maxLength: 64 }), Type.Unknown()),
  missing_field: Type.Optional(Type.Union([Type.String({ maxLength: 64 }), Type.Null()])),
  /** ISO 8601 — dernière sauvegarde. */
  saved_at: Type.String(),
});
export type OnboardingTunnelDraftObject = Static<typeof OnboardingTunnelDraftSchema>;

/** `GET tunnel/draft?step_id=` : le brouillon d'UNE étape, quelle que soit l'étape en cours (relu par un écran après un tour Pulse). */
export const OnboardingTunnelDraftLookupResponseSchema = Type.Object({
  draft: Type.Union([OnboardingTunnelDraftSchema, Type.Null()]),
});
export type OnboardingTunnelDraftLookupResponseObject = Static<typeof OnboardingTunnelDraftLookupResponseSchema>;

/** `POST tunnel/draft` : autosave d'un brouillon d'étape (remplace le brouillon de cette étape). */
export const OnboardingTunnelDraftRequestSchema = Type.Object({
  step_id: Type.String({ minLength: 1, maxLength: 32 }),
  fields: Type.Record(Type.String({ maxLength: 64 }), Type.Unknown()),
  missing_field: Type.Optional(Type.Union([Type.String({ maxLength: 64 }), Type.Null()])),
});
export type OnboardingTunnelDraftRequestObject = Static<typeof OnboardingTunnelDraftRequestSchema>;

/**
 * Écran 11 bis : les mots du pro, gardés BRUTS dans `professional.about_raw` (jamais réécrits par
 * Pulse). `professional.description` n'est renseignée que si elle était vide : on ne l'écrase pas.
 */
export const OnboardingTunnelAboutRequestSchema = Type.Object({
  text: Type.String({ minLength: 1, maxLength: 4000 }),
  mode: Type.Union([Type.Literal('vocal'), Type.Literal('texte')]),
});
export type OnboardingTunnelAboutRequestObject = Static<typeof OnboardingTunnelAboutRequestSchema>;

/** Une prestation du résumé réel (récap et fin) : prix déjà formaté par le backend. */
export const OnboardingTunnelSummaryPrestationSchema = Type.Object({
  name: Type.String(),
  /** Prix formaté (« 55 € ») ; absent = sur devis. */
  price: Type.Optional(Type.String({ meta: { is_price: true, source_key: 'price_raw' } })),
  duration_min: Type.Number(),
});

/**
 * Résumé RÉEL de ce que le tunnel a réglé, calculé depuis les données du compte (mêmes sources que
 * les évaluateurs). Nourrit la victoire chiffrée de chaque étape (A1), la carte « Dès maintenant,
 * Propelsia fait pour vous » (18 bis) et le récap ligne par ligne (18 quater). Jamais de texte
 * fixe : des valeurs, que l'écran met en phrase.
 */
export const OnboardingTunnelSummarySchema = Type.Object({
  prestations: Type.Object({
    count: Type.Number(),
    quote_count: Type.Number(),
    /** Les 4 premières, dans l'ordre du catalogue. */
    sample: Type.Array(OnboardingTunnelSummaryPrestationSchema, { maxItems: 4 }),
  }),
  places: Type.Object({
    kinds: Type.Array(Type.Union(Object.values(PlaceKind).map((value) => Type.Literal(value)))),
    /** Ville du lieu sur place, ou centre du lieu en déplacement. */
    city: Type.Optional(Type.String()),
    radius_km: Type.Optional(Type.Number()),
    /** Jours ouverts (0 à 7) et plage horaire la plus courante (« 9 h – 19 h »), si des créneaux existent. */
    open_days: Type.Number(),
    hours_range: Type.Optional(Type.String()),
    /** Confirmation automatique active (pas de validation à la main). */
    auto_confirm: Type.Boolean(),
    /** Rappel la veille actif sur au moins un canal. */
    reminder: Type.Boolean(),
  }),
  about: Type.Object({
    /** Les mots du pro sont enregistrés (`about_raw`). */
    has_raw: Type.Boolean(),
    word_count: Type.Number(),
    /** Une présentation publique existe (`description`). */
    has_description: Type.Boolean(),
  }),
  website: Type.Object({
    published: Type.Boolean(),
    /** Hôte public (domaine actif, sinon sous-domaine), sans schéma. */
    host: Type.Optional(Type.String()),
    /** Identifiant du style appliqué (`draft_config.template`), quand il existe. */
    style: Type.Optional(Type.String()),
  }),
  seo: Type.Object({
    /** Villes et zones desservies (libellés), dans l'ordre enregistré. */
    cities: Type.Array(Type.String()),
    gmb_linked: Type.Boolean(),
    gmb_pushed: Type.Boolean(),
  }),
  payment: Type.Object({
    rule: Type.Optional(Type.Union(Object.values(PrestationPaymentType).map((value) => Type.Literal(value)))),
    percentage: Type.Optional(Type.Number()),
    scope: Type.Optional(Type.Union([Type.Literal('all'), Type.Literal('selected')])),
    /** Prestations concernées par la règle (toutes, ou la sélection). */
    prestation_count: Type.Number(),
    stripe_active: Type.Boolean(),
  }),
  share: Type.Object({
    /** Le pro a partagé ou copié son lien. */
    shared: Type.Boolean(),
    customer_count: Type.Number(),
    /** Au moins une réservation TEST (étape 3 : « Tester comme une cliente », reconnue par le numéro du pro). */
    test_booking_done: Type.Boolean(),
  }),
});
export type OnboardingTunnelSummaryObject = Static<typeof OnboardingTunnelSummarySchema>;

/**
 * Ce qu'on sait déjà pour pré-remplir les écrans (volet B3 : on dit d'où ça vient) : lieux cochés au
 * quiz (écran 12), phrase du quiz ou description trouvée par la détection (écran 11 bis), site du
 * pro donné au quiz ou validé à l'écran 9 (§4.2 du plan), nom de la fiche Google détectée (écran 17).
 */
export const OnboardingTunnelPrefillSchema = Type.Object({
  /** `sur_place` | `deplacement` | `visio` (quiz, écran 6). */
  lieux: Type.Array(Type.String()),
  /** Phrase distinctive du quiz, sinon description lue par la détection ; null sans rien. */
  about: Type.Union([Type.String(), Type.Null()]),
  /** D'où vient `about` : `quiz` | `detection`. */
  about_origin: Type.Optional(Type.Union([Type.Literal('quiz'), Type.Literal('detection')])),
  /** Ville du quiz, sinon ville saisie à l'inscription (sans ville = null). */
  city: Type.Union([Type.String(), Type.Null()]),
  /**
   * Métier principal du compte (libellé du pro, sinon celui du référentiel) ; null quand le compte
   * n'a aucune activité : inscription hors quiz sans détection, l'étape 1 demande alors le métier.
   */
  activity_label: Type.Union([Type.String(), Type.Null()]),
  /** Site officiel du pro (quiz `site_url`, ou source `official` gardée à l'écran 9). */
  site_url: Type.Union([Type.String(), Type.Null()]),
  /** Nom de la fiche Google détectée (source Google gardée à l'écran 9). */
  google_place_name: Type.Union([Type.String(), Type.Null()]),
});
export type OnboardingTunnelPrefillObject = Static<typeof OnboardingTunnelPrefillSchema>;

/** Objectifs de la semaine (écran 16 ter), valeurs servies par le backend (réglables dans l'admin). */
export const OnboardingWeeklyGoalsSchema = Type.Object({
  online_bookings: Type.Number(),
  outside_hours: Type.Number(),
  minutes_saved: Type.Number(),
});

/**
 * « Votre semaine, en vrai » (écrans 16 ter, 19, 20) sur 7 jours glissants : réservations en ligne
 * (réelles, hors test), réservations hors horaires, temps gagné estimé (réservations × 4 min +
 * messages automatiques × 2 min). `mode = objectifs` tant qu'aucune réservation en ligne n'est
 * arrivée : l'écran montre les objectifs et le geste qui y mène ; chaque chiffre réel non nul
 * remplace son objectif individuellement. Jamais trois zéros.
 */
export const OnboardingWeeklyStatsSchema = Type.Object({
  mode: Type.Union([Type.Literal('objectifs'), Type.Literal('reel')]),
  /** ISO — début de la fenêtre (7 jours glissants). */
  since: Type.String(),
  online_bookings: Type.Number(),
  outside_hours: Type.Number(),
  minutes_saved: Type.Number(),
  /** Messages automatiques partis (confirmations, rappels) sur la fenêtre. */
  automatic_messages: Type.Number(),
  goals: OnboardingWeeklyGoalsSchema,
});
export type OnboardingWeeklyStatsObject = Static<typeof OnboardingWeeklyStatsSchema>;

/**
 * La nouvelle du jour (bloc du jour de l'accueil, section I) : la dernière réservation en ligne
 * réelle, sinon rien. Prénom seulement, date déjà formatée par le backend.
 */
export const OnboardingHomeNewsSchema = Type.Object({
  kind: Type.Union([Type.Literal('booking'), Type.Literal('none')]),
  customer_firstname: Type.Optional(Type.String()),
  prestation_name: Type.Optional(Type.String()),
  booked_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'booked_at', with_time: true } }),
  ),
  outside_hours: Type.Optional(Type.Boolean()),
});
export type OnboardingHomeNewsObject = Static<typeof OnboardingHomeNewsSchema>;

// ─── Réponse : tunnel obligatoire (parcours V2) ───────────────────────────────

export const OnboardingTunnelStepStatusSchema = Type.Union([
  Type.Literal('done'),
  Type.Literal('skipped'),
  Type.Literal('current'),
  Type.Literal('todo'),
]);
export type OnboardingTunnelStepStatus = Static<typeof OnboardingTunnelStepStatusSchema>;

export const OnboardingTunnelTaskSchema = Type.Object({
  id: Type.String(),
  label_key: Type.String(),
  done: Type.Boolean(),
  /** 0 à 100 — ratio de l'évaluateur. */
  score: Type.Number(),
  /** Report ouvert sur cette tâche (kind) — la tâche est « reportée », pas « faite ». */
  deferred_kind: Type.Optional(OnboardingDeferredKindSchema),
});
export type OnboardingTunnelTaskObject = Static<typeof OnboardingTunnelTaskSchema>;

export const OnboardingTunnelStepSchema = Type.Object({
  id: Type.String(),
  order: Type.Number(),
  title_key: Type.String(),
  route: Type.String(),
  status: OnboardingTunnelStepStatusSchema,
  skippable: Type.Boolean(),
  required_tasks: Type.Array(OnboardingTunnelTaskSchema),
  optional_tasks: Type.Array(OnboardingTunnelTaskSchema),
});
export type OnboardingTunnelStepObject = Static<typeof OnboardingTunnelStepSchema>;

/**
 * `GET /app/onboarding/tunnel` — l'état du tunnel, calculé depuis les données réelles (jamais
 * stocké, sauf `tunnel_completed_at` / reports / bonus). Lu par la garde du layout `(app)` :
 * `required = true` ⇒ redirection vers `steps[current].route`.
 */
/** Jour de la semaine type sectorielle (grille 0 = lundi), pré-remplissage de l'écran 14. */
export const OnboardingSectoralDaySchema = Type.Object({
  open: Type.Boolean(),
  start: Type.Optional(Type.String()),
  end: Type.Optional(Type.String()),
});

export const OnboardingTunnelResponseSchema = Type.Object({
  flow: OnboardingFlowSchema,
  /** URL publique de réservation du pro (`/book/<slug>`), le « lien vivant ». */
  booking_url: Type.String(),
  /**
   * Démo Pulse générée au quiz (écran 8), servie par le token du lead rattaché au pro tant que le
   * site n'est pas publié et que l'aperçu n'a pas expiré. Absente sinon : l'écran montre le site
   * publié, ou un squelette.
   */
  demo_preview_url: Type.Optional(Type.String()),
  /**
   * Visuels de l'animation de l'écran 8 (spec v3 03/11) : les photos de la démo générée au quiz
   * (pool stock sectoriel ou vraies photos rehébergées), sinon le pool stock du secteur du compte.
   * URLs Thumbor signées ; absent si rien n'est nommable (l'écran se rabat sur la palette).
   */
  demo_visuals: Type.Optional(
    Type.Object({
      hero_url: Type.String(),
      /** Photos suivantes (galerie / prestations), hero exclu. */
      photo_urls: Type.Array(Type.String()),
      logo_url: Type.Optional(Type.String()),
    }),
  ),
  /** Secteur de l'activité principale (`OnboardingSector`), `generic` à défaut. */
  sector: Type.String(),
  /** Semaine type du secteur, 7 jours à partir du lundi — proposée telle quelle à l'étape 2. */
  sectoral_opening_hours: Type.Array(OnboardingSectoralDaySchema, { minItems: 7, maxItems: 7 }),
  /** Le pro DOIT être dans le tunnel (V2 et tunnel non terminé). */
  required: Type.Boolean(),
  current_step_id: Type.Union([Type.String(), Type.Null()]),
  /** Étapes faites ou ignorées. */
  completed_count: Type.Number(),
  total: Type.Number(),
  tunnel_completed_at: Type.Union([Type.String(), Type.Null()]),
  deferred_items: Type.Array(OnboardingDeferredItemSchema),
  /** ISO — fin des 7 premiers jours calendaires (bonus « tout prêt en 7 jours »). */
  bonus_deadline_at: Type.Union([Type.String(), Type.Null()]),
  bonus_earned_at: Type.Union([Type.String(), Type.Null()]),
  steps: Type.Array(OnboardingTunnelStepSchema),
  // ─── Lot 2 bis ───
  /** ISO — début du parcours (inscription), pour la durée réelle de la fin (18 bis). */
  started_at: Type.Union([Type.String(), Type.Null()]),
  /** ISO — dernière écriture du tunnel (étape validée, brouillon, report) ; null = jamais. */
  last_activity_at: Type.Union([Type.String(), Type.Null()]),
  /** Brouillon de l'étape en cours (autosave), s'il existe — écran 13 et pré-remplissage. */
  draft: Type.Union([OnboardingTunnelDraftSchema, Type.Null()]),
  /** Résumé réel de ce qui est réglé (victoires, fin, récap). */
  summary: OnboardingTunnelSummarySchema,
  /** Jours d'essai restants (entiers, ≥ 0) — pastille « Essai Max · N jours » ; null hors essai. */
  trial_days_left: Type.Union([Type.Number(), Type.Null()]),
  /** Ce qu'on sait déjà pour pré-remplir (quiz, détection). */
  prefill: OnboardingTunnelPrefillSchema,
  /**
   * Plan §4.2 (lot 7) : choix du pro qui a déjà un site — `propelsia` (il passe sur le site
   * Propelsia) ou `own_site` (il garde le sien, l'étape 4 est faite sans publication). Null tant
   * qu'il n'a pas choisi (ou qu'aucun site existant n'est connu).
   */
  website_choice: Type.Union([Type.Literal('propelsia'), Type.Literal('own_site'), Type.Null()]),
});
export type OnboardingTunnelResponseObject = Static<typeof OnboardingTunnelResponseSchema>;

/** Plan §4.2 : le pro qui a déjà un site choisit (écran « Vous avez déjà un site », étape 4). */
export const OnboardingTunnelWebsiteChoiceRequestSchema = Type.Object({
  choice: Type.Union([Type.Literal('propelsia'), Type.Literal('own_site')]),
});
export type OnboardingTunnelWebsiteChoiceRequestObject = Static<typeof OnboardingTunnelWebsiteChoiceRequestSchema>;

// ─── Écritures du tunnel ──────────────────────────────────────────────────────

/** « Ignorer pour l'instant » : pose un report (étape 3 entière, Stripe, fiche Google, à-propos…). */
export const OnboardingTunnelSkipRequestSchema = Type.Object({
  kind: OnboardingDeferredKindSchema,
  /** Raison fermée choisie à l'écran (ex. `no_google_profile_yet`), jamais de texte libre. */
  reason: Type.Optional(Type.String({ maxLength: 64 })),
});
export type OnboardingTunnelSkipRequestObject = Static<typeof OnboardingTunnelSkipRequestSchema>;

/** Ferme un report (la chose a été faite, ou abandonnée). */
export const OnboardingTunnelDeferredCloseRequestSchema = Type.Object({
  kind: OnboardingDeferredKindSchema,
});
export type OnboardingTunnelDeferredCloseRequestObject = Static<typeof OnboardingTunnelDeferredCloseRequestSchema>;

/** Étape 6 : règle de paiement enregistrée AVANT Stripe, appliquée dès que Stripe encaisse. */
export const OnboardingTunnelPaymentRuleRequestSchema = Type.Object({
  payment_type: Type.Union(Object.values(PrestationPaymentType).map((value) => Type.Literal(value))),
  percentage: Type.Optional(Type.Integer({ minimum: 1, maximum: 100 })),
  scope: Type.Union([Type.Literal('all'), Type.Literal('selected')]),
  prestation_ids: Type.Optional(Type.Array(Type.Integer(), { maxItems: 200 })),
});
export type OnboardingTunnelPaymentRuleRequestObject = Static<typeof OnboardingTunnelPaymentRuleRequestSchema>;

/** Étape 3 : le pro a partagé ou copié son lien (feuille native, copie, WhatsApp, Instagram…). */
export const OnboardingTunnelLinkSharedRequestSchema = Type.Object({
  channel: Type.Optional(Type.String({ maxLength: 32 })),
});
export type OnboardingTunnelLinkSharedRequestObject = Static<typeof OnboardingTunnelLinkSharedRequestSchema>;

/** Le pro a validé une étape depuis son écran : le serveur recalcule et émet les jalons. */
export const OnboardingTunnelStepCompleteRequestSchema = Type.Object({
  step_id: Type.String({ minLength: 1, maxLength: 32 }),
});
export type OnboardingTunnelStepCompleteRequestObject = Static<typeof OnboardingTunnelStepCompleteRequestSchema>;

// ─── Accueil « Et maintenant » (après le tunnel, spec v4 section D) ───────────

export const OnboardingHomePrioritySchema = Type.Object({
  /** `subscribe` | `first_appointments` | `blog` | `calendar_sync` | `import_customers` | `customize_site` | `holidays`. */
  id: Type.String(),
  /** Ordre fixe 1 à 7. */
  order: Type.Number(),
  label_key: Type.String(),
  done: Type.Boolean(),
  menu_route: Type.String(),
  menu_sub_tab: Type.Optional(Type.String()),
  /** Clé du libellé de la page du menu (« Agenda », « Communication › Blog ») — sous-titre de la ligne. */
  page_label_key: Type.String(),
  /** Durée annoncée, en minutes. */
  duration_min: Type.Number(),
});
export type OnboardingHomePriorityObject = Static<typeof OnboardingHomePrioritySchema>;

export const OnboardingHomeResponseSchema = Type.Object({
  flow: OnboardingFlowSchema,
  tunnel_completed_at: Type.Union([Type.String(), Type.Null()]),
  bonus_deadline_at: Type.Union([Type.String(), Type.Null()]),
  bonus_earned_at: Type.Union([Type.String(), Type.Null()]),
  /** Jours écoulés depuis l'inscription (0 le jour même). */
  days_since_signup: Type.Number(),
  booking_url: Type.String(),
  /** Priorités APPLICABLES au pro, dans l'ordre, faites ou non (jamais plus de 7). */
  priorities: Type.Array(OnboardingHomePrioritySchema),
  /** Reports encore ouverts (« ignoré pendant le parcours »). */
  deferred_open: Type.Array(OnboardingDeferredItemSchema),
  // ─── Lot 2 bis ───
  /** « Votre semaine, en vrai » ou ses objectifs (16 ter) — jamais trois zéros. */
  weekly: OnboardingWeeklyStatsSchema,
  /** La nouvelle du jour pour le bloc du jour (dernière réservation en ligne), ou `none`. */
  news: OnboardingHomeNewsSchema,
  /** Visites du lien de réservation sur 7 jours glissants, quand on sait les compter ; null sinon. */
  link_visits_7d: Type.Union([Type.Number(), Type.Null()]),
});
export type OnboardingHomeResponseObject = Static<typeof OnboardingHomeResponseSchema>;

// ─── Tunnel v4 · étape 1 : écran 9 (ce qu'on a trouvé) et écran 11 (la liste) ────────────────

export const OnboardingTunnelDetectionStatusSchema = Type.Union([
  /** Aucune détection lancée (compte hors parcours v2, consentement refusé, pas de téléphone ni de nom). */
  Type.Literal('none'),
  Type.Literal('pending'),
  Type.Literal('done'),
]);
export type OnboardingTunnelDetectionStatus = Static<typeof OnboardingTunnelDetectionStatusSchema>;

/**
 * État de la détection de fond pour l'écran 9 : les sources trouvées, ce qu'on en a tiré (totaux,
 * jamais le détail), et si le pro a déjà revu. `extracted` reste côté serveur : la liste de
 * l'écran 11 vient de `GET tunnel/offer-draft`.
 */
export const OnboardingTunnelDetectionResponseSchema = Type.Object({
  status: OnboardingTunnelDetectionStatusSchema,
  /** `done` sans rien d'exploitable (pas de fiche, pas de site) : l'écran 9 est sauté. */
  no_match: Type.Boolean(),
  reviewed: Type.Boolean(),
  sources: Type.Array(DetectionSourceSummarySchema),
  /** Nom et adresse de l'établissement tel que trouvé (confirmation « c'est bien vous ? »). */
  business: Type.Optional(
    Type.Object({
      name: Type.String(),
      address: Type.Optional(Type.String()),
      phone: Type.Optional(Type.String()),
    }),
  ),
  summary: Type.Object({
    prestations: Type.Integer({ minimum: 0 }),
    /** Prestations trouvées AVEC un prix. */
    priced: Type.Integer({ minimum: 0 }),
    photos: Type.Integer({ minimum: 0 }),
    reviews: Type.Integer({ minimum: 0 }),
    has_hours: Type.Boolean(),
    has_about: Type.Boolean(),
  }),
});
export type OnboardingTunnelDetectionResponseObject = Static<typeof OnboardingTunnelDetectionResponseSchema>;

/** Écran 9 validé : les clés des sources décochées (`google` ou l'URL). Vide = tout est gardé. */
export const OnboardingTunnelDetectionReviewRequestSchema = Type.Object({
  rejected_keys: Type.Array(Type.String({ maxLength: 2048 }), { maxItems: 20 }),
});
export type OnboardingTunnelDetectionReviewRequestObject = Static<typeof OnboardingTunnelDetectionReviewRequestSchema>;

export const OnboardingTunnelOfferOriginSchema = Type.Union([
  /** Déjà dans le catalogue (génération, reprise) : rien à confirmer. */
  Type.Literal('catalog'),
  /** Trouvée sur une source validée à l'écran 9 (prix réel si la source en avait un). */
  Type.Literal('detection'),
  /** Cochée au quiz par le pro ; prix médian du métier, à confirmer. */
  Type.Literal('quiz'),
  /** Défaut du métier (aucune autre matière) ; prix médian, à confirmer. */
  Type.Literal('default'),
  /** Proposée par Pulse à l'écran 10 (`POST tunnel/offer-draft/propose`) : dans la liste, pas dans le compte. */
  Type.Literal('pulse'),
]);
export type OnboardingTunnelOfferOrigin = Static<typeof OnboardingTunnelOfferOriginSchema>;

/**
 * Clé, dans `onboarding_state.tunnel_draft`, de la liste proposée par Pulse à l'écran 10. Ce n'est pas
 * une étape (l'autosave `POST tunnel/draft` refuse cette clé) : le brouillon `offer` de l'écran
 * peut être remplacé sans toucher aux propositions de Pulse. Effacée à la validation de la liste.
 */
export const ONBOARDING_TUNNEL_PULSE_OFFER_DRAFT_KEY = 'offer_pulse';

/**
 * Une prestation proposée par Pulse : mêmes champs et mêmes bornes que `OnboardingTunnelPrestationSchema`
 * (la liste validée), plus « à confirmer ». Les `description` sont lues par le modèle dans le schéma
 * de l'outil MCP : unités explicites (euros, minutes), parce qu'il a envoyé des centimes et des
 * chaînes (29/10). `additionalProperties: false` : une clé inventée (`price`) fait un 400 lisible au
 * lieu d'une prestation « sur devis » en silence.
 */
export const OnboardingTunnelOfferProposedPrestationSchema = Type.Object(
  {
    prestation_id: Type.Optional(
      Type.Integer({
        minimum: 1,
        description: 'Seulement pour une prestation déjà dans le catalogue (id de getOfferDraft).',
      }),
    ),
    name: Type.String({
      minLength: 1,
      maxLength: 255,
      description: 'Nom tel que le client le lira, ex. « Pose gel complète ».',
    }),
    price_eur: Type.Optional(
      Type.Number({
        minimum: 0,
        maximum: 100000,
        description:
          'Prix TTC en EUROS, nombre (55 pour 55 €, jamais en centimes). JAMAIS 0 pour « sur devis » : 0 € voudrait dire gratuit. Sur devis = `on_quote: true` sans price_eur. Absent sans on_quote : une prestation déjà au catalogue garde son prix.',
      }),
    ),
    on_quote: Type.Optional(
      Type.Boolean({
        description:
          'true = prestation « sur devis » (le client demande un devis, aucun prix affiché). Remplace tout prix connu ; price_eur est alors absent.',
      }),
    ),
    duration_min: Type.Integer({
      minimum: 5,
      maximum: 24 * 60,
      description: 'Durée en MINUTES, nombre entier (90 pour 1 h 30).',
    }),
    category: Type.Optional(
      Type.String({
        minLength: 1,
        maxLength: 255,
        description: 'Famille, ex. « Ongles » ; absente = « Mes prestations ».',
      }),
    ),
    description: Type.Optional(
      Type.String({ maxLength: 2000, description: 'Une ou deux phrases pour le client, sans prix ni durée.' }),
    ),
    to_confirm: Type.Optional(
      Type.Boolean({
        description: 'true si le prix ou la durée vient de toi (valeur courante du métier) et pas du pro.',
      }),
    ),
  },
  { additionalProperties: false },
);
export type OnboardingTunnelOfferProposedPrestationObject = Static<
  typeof OnboardingTunnelOfferProposedPrestationSchema
>;

const HOUR_PATTERN = '^([01]\\d|2[0-3]):[0-5]\\d$';

/** Un jour de la semaine type proposé par Pulse (étape 2) : seulement les jours à changer. */
export const OnboardingTunnelPlacesHoursProposedDaySchema = Type.Object(
  {
    weekday: Type.Integer({ minimum: 1, maximum: 7, description: 'Jour ISO : 1 = lundi … 7 = dimanche.' }),
    open: Type.Boolean({ description: 'false = fermé ce jour-là.' }),
    start: Type.Optional(
      Type.String({ pattern: HOUR_PATTERN, description: 'Ouverture « HH:MM » sur 24 h, ex. 09:00.' }),
    ),
    end: Type.Optional(Type.String({ pattern: HOUR_PATTERN, description: 'Fermeture « HH:MM » sur 24 h, ex. 19:00.' })),
  },
  { additionalProperties: false },
);
export type OnboardingTunnelPlacesHoursProposedDayObject = Static<typeof OnboardingTunnelPlacesHoursProposedDaySchema>;

/**
 * `POST tunnel/places-hours/propose` (outil Pulse de l'étape 2, écrans 12 et 14) : Pulse remplit les
 * champs de l'ÉCRAN (brouillon `places_hours`), il ne crée ni lieu ni planning ; c'est « C'est bon »
 * qui écrit. Chaque champ est optionnel : seul ce qui est donné change, le reste de l'écran reste.
 * La ville du déplacement (adresse géocodée) se saisit sur l'écran, pas ici.
 */
export const OnboardingTunnelPlacesHoursProposeRequestSchema = Type.Object(
  {
    place_kinds: Type.Optional(
      Type.Array(Type.Enum(PlaceKind), {
        minItems: 1,
        maxItems: 4,
        uniqueItems: true,
        description:
          'Modalités de rendez-vous, remplace la sélection : ESTABLISHMENT (sur place), TRAVELING (à domicile / en déplacement), VIDEO (visio), PHONE (téléphone).',
      }),
    ),
    radius_km: Type.Optional(
      Type.Integer({ minimum: 1, maximum: 200, description: 'Rayon de déplacement en kilomètres (nombre entier).' }),
    ),
    video_link: Type.Optional(Type.String({ maxLength: 500, description: 'Lien de visio (URL complète).' })),
    days: Type.Optional(
      Type.Array(OnboardingTunnelPlacesHoursProposedDaySchema, {
        minItems: 1,
        maxItems: 7,
        description: 'Seulement les jours à changer ; les autres gardent les horaires déjà sur l’écran.',
      }),
    ),
  },
  { additionalProperties: false, minProperties: 1 },
);
export type OnboardingTunnelPlacesHoursProposeRequestObject = Static<
  typeof OnboardingTunnelPlacesHoursProposeRequestSchema
>;

/**
 * `POST tunnel/offer-draft/propose` (outil Pulse de l'écran 10) : la liste COMPLÈTE des prestations
 * que Pulse a cadrées avec le pro. Elle remplace la proposition précédente de Pulse et n'écrit rien
 * dans le catalogue : c'est le pro qui valide sur l'écran 11 (« C'est bien ça, je valide »).
 */
export const OnboardingTunnelOfferProposeRequestSchema = Type.Object({
  prestations: Type.Array(OnboardingTunnelOfferProposedPrestationSchema, { minItems: 1, maxItems: 40 }),
});
export type OnboardingTunnelOfferProposeRequestObject = Static<typeof OnboardingTunnelOfferProposeRequestSchema>;

export const OnboardingTunnelOfferPriceOriginSchema = Type.Union([
  /** Prix lu sur une source gardée à l'écran 9 (site, page de réservation). */
  Type.Literal('source'),
  /** Prix indicatif : médiane du métier, défaut, estimation du quiz. */
  Type.Literal('suggested'),
]);
export type OnboardingTunnelOfferPriceOrigin = Static<typeof OnboardingTunnelOfferPriceOriginSchema>;

export const OnboardingTunnelOfferDraftItemSchema = Type.Object({
  prestation_id: Type.Optional(Type.Integer({ minimum: 1 })),
  name: Type.String(),
  /** Absent = aucun prix connu (ou prestation déjà « sur devis ») : validée telle quelle en `QUOTE`, un tap pour fixer un prix. */
  price_eur: Type.Optional(Type.Number({ minimum: 0 })),
  /** Pulse l'a voulue « sur devis » (écran 10) : l'écran n'y remet pas un prix qu'il avait sous les yeux. */
  on_quote: Type.Optional(Type.Boolean()),
  duration_min: Type.Integer({ minimum: 5 }),
  category: Type.Optional(Type.String()),
  description: Type.Optional(Type.String()),
  origin: OnboardingTunnelOfferOriginSchema,
  /** Prix ou durée proposés (médiane du métier, défaut) et non confirmés par le pro. */
  to_confirm: Type.Boolean(),
  /**
   * D'où vient LE PRIX de la ligne (recette 03/11 : « Trouvé sur votre fiche Google ou votre site,
   * avec vos prix » s'affichait pour des prix médians). `source` = lu tel quel sur une source gardée
   * à l'écran 9 ; `suggested` = valeur indicative (médiane du métier, défaut, estimation du quiz).
   * Absent : prix du catalogue ou de Pulse, rien à annoncer.
   */
  price_origin: Type.Optional(OnboardingTunnelOfferPriceOriginSchema),
  /**
   * Ligne reprise par Pulse SANS prix : le prix affiché est celui de la ligne existante (catalogue,
   * liste pré-remplie), pas un prix donné par Pulse. L'écran garde alors le prix que le pro a tapé
   * (recette 31/10 : un prix saisi ne doit jamais être écrasé au retour du chat).
   */
  price_kept: Type.Optional(Type.Boolean()),
});
export type OnboardingTunnelOfferDraftItemObject = Static<typeof OnboardingTunnelOfferDraftItemSchema>;

/** Liste pré-remplie de l'écran 11 : catalogue réel, sinon détection validée + quiz, sinon défauts. */
export const OnboardingTunnelOfferDraftResponseSchema = Type.Object({
  prestations: Type.Array(OnboardingTunnelOfferDraftItemSchema),
  /**
   * Pages gardées à l'écran 9 d'où viennent les lignes `origin = detection` (hôtes, séparés par des
   * virgules, site officiel en premier) : nomme la liste et les prix `price_origin = source`. La
   * fiche Google n'y figure jamais (elle ne porte pas de prestations). Absent : rien de nommable,
   * l'écran ne cite jamais « votre fiche Google ou votre site » sans source identifiable.
   */
  source_label: Type.Optional(Type.String()),
});
export type OnboardingTunnelOfferDraftResponseObject = Static<typeof OnboardingTunnelOfferDraftResponseSchema>;

/**
 * Écran 11 bis : transcription du vocal « je me présente » (Whisper, même pipeline que Pulse).
 * `silent` = le provider a écouté mais n'a rien entendu ; `failed` = aucun provider n'a répondu.
 * Dans les deux cas le texte est absent et l'écran rend la main au champ texte.
 */
export const OnboardingTunnelTranscriptionResponseSchema = Type.Object({
  outcome: Type.Union([Type.Literal('text'), Type.Literal('silent'), Type.Literal('failed')]),
  /** Transcription brute, gardée telle quelle (origine = pro). */
  text: Type.Optional(Type.String()),
});
export type OnboardingTunnelTranscriptionResponseObject = Static<typeof OnboardingTunnelTranscriptionResponseSchema>;

// ─── Lot 5 · Pulse rédige (spec section H) : textes versionnés `pro_generated_text` ─────────

export const PulseTextKindSchema = Type.Union(
  Object.values(PulseTextKind).map((value): ReturnType<typeof Type.Literal> => Type.Literal(value)),
);

/** Une version d'un texte rédigé par Pulse (la dernière par `kind` + `target_id` dans `GET tunnel/writing`). */
export const OnboardingTunnelWritingTextSchema = Type.Object({
  kind: PulseTextKindSchema,
  /** Prestation visée (`SERVICE_DESCRIPTION`), sinon null. */
  target_id: Type.Union([Type.Number(), Type.Null()]),
  /** Nom de la prestation visée, pour l'encart « pendant qu'on parle, Pulse rédige ». */
  target_label: Type.Optional(Type.String()),
  version: Type.Number(),
  content: Type.String(),
  /** Écrit dans le vrai champ (description, à-propos…) à cette date ; null = proposition. */
  applied_at: Type.Union([Type.String(), Type.Null()]),
  created_at: Type.String(),
});
export type OnboardingTunnelWritingTextObject = Static<typeof OnboardingTunnelWritingTextSchema>;

export const OnboardingTunnelWritingResponseSchema = Type.Object({
  texts: Type.Array(OnboardingTunnelWritingTextSchema),
});
export type OnboardingTunnelWritingResponseObject = Static<typeof OnboardingTunnelWritingResponseSchema>;

/** « Une autre » = `another: true` : nouvelle version, différente de la précédente. */
export const OnboardingTunnelWritingGenerateRequestSchema = Type.Object({
  kind: PulseTextKindSchema,
  target_id: Type.Optional(Type.Number()),
  another: Type.Optional(Type.Boolean()),
});
export type OnboardingTunnelWritingGenerateRequestObject = Static<typeof OnboardingTunnelWritingGenerateRequestSchema>;

/** « Je garde » : la version est écrite dans le vrai champ du compte. */
export const OnboardingTunnelWritingApplyRequestSchema = Type.Object({
  kind: PulseTextKindSchema,
  target_id: Type.Optional(Type.Number()),
  version: Type.Number({ minimum: 1 }),
});
export type OnboardingTunnelWritingApplyRequestObject = Static<typeof OnboardingTunnelWritingApplyRequestSchema>;
